import React, { Component, createRef } from 'react';
import styles from './styles.module.css';
import { IoSearchSharp, IoChevronDown } from 'react-icons/io5';
import { Icon } from '../Icon';

export interface Option {
  value: string;
  label: string;
}

export interface SelectInputProps {
  label: string;
  value: Option | null;
  setValue: (option: Option | null) => void;
  options: Option[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  filterable?: boolean;
}

interface SelectInputState {
  searchTerm: string;
}

export class SelectInput extends Component<SelectInputProps, SelectInputState> {
  private dropdownRef = createRef<HTMLDivElement>();
  private triggerRef = createRef<HTMLDivElement>();
  private inputRef = createRef<HTMLInputElement>();
  private controlId: string;
  private closeTimeout: number | null = null;

  constructor(props: SelectInputProps) {
    super(props);
    this.controlId =
      props.id ?? `select-input-${Math.random().toString(36).slice(2, 9)}`;
    this.state = {
      searchTerm: props.value?.label ?? '',
    };
  }

  private skipOpenOnFocus = false;

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps: Readonly<SelectInputProps>) {
    if (prevProps.value?.label !== this.props.value?.label) {
      this.setState({ searchTerm: this.props.value?.label ?? '' });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.closeTimeout) {
      window.clearTimeout(this.closeTimeout);
    }
  }

  handleClickOutside = (event: MouseEvent) => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target as Node) &&
      this.triggerRef.current &&
      !this.triggerRef.current.contains(event.target as Node)
    ) {
      this.props.setIsOpen(false);
    }
  };

  handleOptionClick = (option: Option) => {
    if (this.props.disabled) {
      return;
    }

    this.props.setValue(option);
    this.props.setIsOpen(false);
    if (this.props.filterable) {
      this.setState({ searchTerm: option.label });
      this.skipOpenOnFocus = true;
    }
    if (this.props.filterable) {
      this.inputRef.current?.focus();
    } else {
      this.triggerRef.current?.focus();
    }
  };

  handleKeyDown = (e: React.KeyboardEvent) => {
    if (this.props.disabled) {
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      this.props.setIsOpen(!this.props.isOpen);
    } else if (e.key === 'Escape') {
      this.props.setIsOpen(false);
    }
  };

  handleInputFocus = () => {
    if (this.props.disabled) {
      return;
    }
    if (this.skipOpenOnFocus) {
      this.skipOpenOnFocus = false;
      return;
    }
    this.props.setIsOpen(true);
  };

  handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return;
    }

    if (e.key === 'Escape') {
      this.props.setIsOpen(false);
      this.inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      this.props.setIsOpen(true);
    }
  };

  handleOptionKeyDown = (e: React.KeyboardEvent, option: Option) => {
    if (this.props.disabled) {
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      this.handleOptionClick(option);
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return;
    }

    this.setState({ searchTerm: e.target.value });
    if (!this.props.isOpen) {
      this.props.setIsOpen(true);
    }
  };

  handleInputBlur = () => {
    if (this.props.disabled) {
      return;
    }
    this.closeTimeout = window.setTimeout(() => {
      const active = document.activeElement;
      const clickedInsideDropdown =
        this.dropdownRef.current &&
        active &&
        this.dropdownRef.current.contains(active);
      const clickedInsideTrigger =
        this.triggerRef.current &&
        active &&
        this.triggerRef.current.contains(active);
      if (!clickedInsideDropdown && !clickedInsideTrigger) {
        this.props.setIsOpen(false);
      }
    }, 0);
  };

  render() {
    const {
      label,
      value,
      options,
      isOpen,
      placeholder = 'Selecione...',
      disabled = false,
      leadingIcon,
      trailingIcon,
      className,
      labelClassName,
      inputClassName,
      filterable = false,
    } = this.props;
    const { searchTerm } = this.state;

    const labelId = `${this.controlId}-label`;
    const dropdownId = `${this.controlId}-dropdown`;
    const displayValue = filterable ? searchTerm : value?.label ?? placeholder;
    const hasValue = filterable ? Boolean(searchTerm) : Boolean(value);
    const filteredOptions = filterable
      ? options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;
    const handleToggleOpen = () => {
      if (disabled) return;
      const next = filterable ? true : !isOpen;
      this.props.setIsOpen(next);
      if (filterable) {
        this.inputRef.current?.focus();
      }
    };

    const startIcon =
      leadingIcon === null
        ? null
        : leadingIcon ?? (
            <Icon
              icon={IoSearchSharp}
              className={styles.customIconSearch}
              fontSize={24}
            />
          );
    const endIcon =
      trailingIcon === null
        ? null
        : trailingIcon ?? (
            <Icon
              icon={IoChevronDown}
              className={styles.customIcon}
              fontSize={20}
            />
          );

    const wrapSelectClasses = [
      styles.wrapSelect,
      isOpen && !disabled ? styles.wrapSelectOpen : '',
      disabled ? styles.wrapSelectDisabled : '',
    ]
      .filter(Boolean)
      .join(' ');

    const selectedValueClasses = [
      styles.selectedValue,
      hasValue ? styles.selectedValueFilled : '',
    ]
      .filter(Boolean)
      .join(' ');

    const dropdownClasses = [styles.dropdown, isOpen ? styles.open : '']
      .filter(Boolean)
      .join(' ');

    const wrapperClasses = [
      styles.formControl,
      isOpen && !disabled ? styles.formControlOpen : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const labelClasses = [styles.label, labelClassName]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        <label id={labelId} className={labelClasses} htmlFor={this.controlId}>
          {label}
        </label>
        <div
          ref={this.triggerRef}
          role={filterable ? undefined : 'combobox'}
          aria-haspopup={filterable ? undefined : 'listbox'}
          aria-expanded={filterable ? undefined : isOpen}
          aria-labelledby={filterable ? undefined : labelId}
          aria-controls={filterable ? undefined : dropdownId}
          aria-disabled={disabled}
          tabIndex={filterable ? -1 : disabled ? -1 : 0}
          id={filterable ? undefined : this.controlId}
          className={`${wrapSelectClasses} ${inputClassName ?? ''}`.trim()}
          onClick={handleToggleOpen}
          onKeyDown={filterable ? undefined : this.handleKeyDown}
        >
          {startIcon}

          {filterable ? (
            <input
              ref={this.inputRef}
              type="text"
              aria-autocomplete="list"
              aria-controls={dropdownId}
              aria-expanded={isOpen}
              aria-labelledby={labelId}
              aria-haspopup="listbox"
              role="combobox"
              disabled={disabled}
              placeholder={placeholder}
              value={displayValue}
              id={this.controlId}
              className={styles.input}
              onChange={this.handleInputChange}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
              onKeyDown={this.handleInputKeyDown}
            />
          ) : (
            <div className={selectedValueClasses}>{displayValue}</div>
          )}

          {endIcon}
        </div>
        {isOpen && !disabled && (
          <div
            ref={this.dropdownRef}
            id={dropdownId}
            role="listbox"
            aria-labelledby={labelId}
            className={dropdownClasses}
          >
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>Sem resultados</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={value?.value === option.value}
                  tabIndex={0}
                  className={styles.option}
                  onClick={() => this.handleOptionClick(option)}
                  onKeyDown={(e) => this.handleOptionKeyDown(e, option)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }
}
